/**
 * ChatTray — bottom-right floating chat panel (Tier 2).
 *
 * This is the primary daily-use chat interface, enabling multitasking
 * without leaving the current page. Slides up from the bottom-right corner.
 *
 * States: sidebar → new conversation panel → active chat window.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { fetchConversations, fetchConversation, createConversation, archiveConversation, pinConversation } from '../../api/chat';
import { fetchAllSecurityUsers } from '../../api/security';
import type { ConversationResponse } from '../../api/chat';
import type { SecurityUserResponse } from '../../api/security';
import { onChatEvent, ChatHubEvents, joinConversationGroup } from '../../api/chatConnection';
import ChatSidebar from './ChatSidebar';
import ChatWindow from './ChatWindow';
import NewConversationPanel from './NewConversationPanel';
import styles from './Chat.module.css';

type TrayView = 'sidebar' | 'newConversation' | 'chat';

interface ChatTrayProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserGuid: string;
  /**
   * DEV-1256. Imperative "open a DM with this user" trigger. Set by the
   * App-level event listener that handles `l4e:open-chat-with` events
   * dispatched by surfaces like Action Center / Engagements task trays
   * (so the user can message a task's contact without leaving the work
   * surface they're on).
   *
   * On change to a non-null value the tray opens an existing direct
   * conversation with that user, OR creates a new one, then resets the
   * value via `onStartChatWithConsumed` so the same GUID can be sent
   * again later (e.g. user closes the tray, clicks Message again).
   */
  startChatWithUserGuid?: string | null;
  onStartChatWithConsumed?: () => void;
}

export default function ChatTray({
  isOpen,
  onClose,
  currentUserGuid,
  startChatWithUserGuid,
  onStartChatWithConsumed,
}: ChatTrayProps) {
  const { t } = useAppTranslation('chat');
  const [view, setView] = useState<TrayView>('sidebar');
  const [conversations, setConversations] = useState<ConversationResponse[]>([]);
  const [activeConversation, setActiveConversation] = useState<ConversationResponse | null>(null);
  const [platformUsers, setPlatformUsers] = useState<SecurityUserResponse[]>([]);
  const [loading, setLoading] = useState(false);

  // Enrich conversation participants with correct user data from platformUsers.
  // The backend may return the authenticated user's avatar for all participants.
  const enrichedConversations = useMemo(() => {
    if (platformUsers.length === 0) return conversations;
    const userMap = new Map<string, SecurityUserResponse>();
    for (const u of platformUsers) {
      userMap.set(u.userGuid.toUpperCase(), u);
    }
    return conversations.map((conv) => ({
      ...conv,
      participants: conv.participants.map((p) => {
        const realUser = userMap.get(p.userGuid.toUpperCase());
        if (!realUser) return p;
        // Always prefer realUser data — do NOT fall back to p.profileImageUrl
        // because the backend may return the authenticated user's avatar for all participants.
        // An empty string means "no avatar" (show initials), which is correct.
        return {
          ...p,
          displayName: realUser.displayName || realUser.name || p.displayName,
          screenName: realUser.screenName || p.screenName,
          profileImageUrl: realUser.profileImageUrl,
        };
      }),
    }));
  }, [conversations, platformUsers]);

  // Load conversations + users when tray opens.
  // Each fetch is independent so one failure doesn't block the others.
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);

    const loadConversations = fetchConversations()
      .then(setConversations)
      .catch((err) => console.error('[ChatTray] Failed to load conversations:', err));

    const loadUsers = fetchAllSecurityUsers()
      .then(setPlatformUsers)
      .catch((err) => console.error('[ChatTray] Failed to load users:', err));

    Promise.allSettled([loadConversations, loadUsers])
      .finally(() => setLoading(false));
  }, [isOpen]);

  // Listen for new messages and new conversations to update conversation list
  useEffect(() => {
    if (!isOpen) return;

    const refreshConvs = () => {
      fetchConversations()
        .then(setConversations)
        .catch(() => { /* silently fail */ });
    };

    const unsub1 = onChatEvent(ChatHubEvents.NewMessage, refreshConvs);
    const unsub2 = onChatEvent(ChatHubEvents.ConversationInvite, (data: any) => {
      // Join the SignalR group for the new conversation so we receive messages
      if (data?.conversationId) {
        joinConversationGroup(data.conversationId).catch(() => {});
      }
      refreshConvs();
    });

    // Refresh active conversation when participants change
    const refreshActiveConv = (data: any) => {
      const convId = data?.conversationId;
      if (convId) {
        refreshConvs();
        // Also refresh the active conversation object so its participants are current
        setActiveConversation((prev) => {
          if (prev && prev.id === convId) {
            fetchConversation(convId)
              .then((updated) => setActiveConversation(updated))
              .catch(() => {});
          }
          return prev;
        });
      }
    };
    const unsub3 = onChatEvent(ChatHubEvents.ParticipantJoined, refreshActiveConv);
    const unsub4 = onChatEvent(ChatHubEvents.ParticipantLeft, refreshActiveConv);

    return () => { unsub1(); unsub2(); unsub3(); unsub4(); };
  }, [isOpen]);

  // Reset view when tray closes
  useEffect(() => {
    if (!isOpen) {
      setView('sidebar');
      setActiveConversation(null);
    }
  }, [isOpen]);

  const handleSelectConversation = useCallback(async (id: string) => {
    try {
      const conv = await fetchConversation(id);
      setActiveConversation(conv);
      setView('chat');
    } catch (err) {
      console.error('[ChatTray] Failed to load conversation:', err);
    }
  }, []);

  const handleNewConversation = useCallback(() => {
    setView('newConversation');
  }, []);

  /**
   * DEV-1256. Open or create a DM with a specific user, then surface it as
   * the active conversation in the tray. Mirrors MessagesPage's
   * handleStartChatWithContact (which the Action Center previously routed
   * to via /messages?startChatWith=).
   *
   * Order:
   *   1. Look in the loaded conversation list for an existing direct
   *      convo with this user. If found, just switch to it.
   *   2. If none, POST /api/chat/conversations to create a new direct
   *      convo. If the backend reports one already exists (race or stale
   *      cache), pick it up from err.body.conversationId and switch.
   */
  const handleStartChatWithContact = useCallback(async (userGuid: string) => {
    try {
      const existing = conversations.find((c) =>
        c.conversationType === 'direct' &&
        c.participants.some((p) => p.userGuid.toUpperCase() === userGuid.toUpperCase())
      );
      if (existing) {
        setActiveConversation(existing);
        setView('chat');
        return;
      }
      const conv = await createConversation({
        conversationType: 'direct',
        participantGuids: [userGuid],
      });
      setConversations((prev) => [conv, ...prev]);
      setActiveConversation(conv);
      setView('chat');
      joinConversationGroup(conv.id).catch(() => {});
    } catch (err: any) {
      // Backend may report an existing convo race-condition style.
      if (err?.body?.conversationId) {
        try {
          const existingId = err.body.conversationId;
          const existingConv = await fetchConversation(existingId);
          setConversations((prev) =>
            prev.some((c) => c.id === existingId) ? prev : [existingConv, ...prev],
          );
          setActiveConversation(existingConv);
          setView('chat');
          return;
        } catch (fetchErr) {
          console.error('[ChatTray] Failed to fetch existing conversation:', fetchErr);
        }
      }
      console.error('[ChatTray] Failed to start chat with user:', err);
    }
  }, [conversations]);

  /**
   * DEV-1256. Consume the imperative startChatWithUserGuid prop on change.
   * Fires once per non-null value; clears the prop via the callback so a
   * re-trigger with the same GUID still works. Guarded by:
   *   - currentUserGuid populated (auth ready)
   *   - target != self (can't DM yourself; backend would 400)
   *   - conversations loaded (so the dedupe path can hit before create)
   */
  useEffect(() => {
    if (!startChatWithUserGuid) return;
    if (!isOpen) return;
    if (loading) return; // wait for conversations to hydrate so dedupe works
    const me = currentUserGuid.toUpperCase();
    const them = startChatWithUserGuid.toUpperCase();
    if (me === them) {
      onStartChatWithConsumed?.();
      return;
    }
    void handleStartChatWithContact(startChatWithUserGuid);
    onStartChatWithConsumed?.();
  }, [startChatWithUserGuid, isOpen, loading, currentUserGuid, handleStartChatWithContact, onStartChatWithConsumed]);

  const handleConversationCreated = useCallback((conv: ConversationResponse) => {
    setConversations((prev) => [conv, ...prev]);
    setActiveConversation(conv);
    setView('chat');
    // Join the SignalR group so we can receive messages in real-time
    joinConversationGroup(conv.id).catch(() => {});
  }, []);

  const handleArchiveConversation = useCallback(async (id: string) => {
    try {
      await archiveConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      // If the archived conversation was active, go back to sidebar
      if (activeConversation?.id === id) {
        setActiveConversation(null);
        setView('sidebar');
      }
    } catch (err) {
      console.error('[ChatTray] Failed to archive conversation:', err);
    }
  }, [activeConversation]);

  const handlePinConversation = useCallback(async (id: string) => {
    try {
      const result = await pinConversation(id);
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, isPinned: result.isPinned } : c))
      );
    } catch (err) {
      console.error('[ChatTray] Failed to pin conversation:', err);
    }
  }, []);

  const handleBack = useCallback(() => {
    if (view === 'chat') {
      setActiveConversation(null);
      setView('sidebar');
    } else if (view === 'newConversation') {
      setView('sidebar');
    }
  }, [view]);

  // Get the other participant in a direct message conversation
  const conversationPartner = (view === 'chat' && activeConversation && activeConversation.conversationType === 'direct')
    ? activeConversation.participants.find(
        (p) => p.userGuid.toUpperCase() !== currentUserGuid.toUpperCase()
      )
    : null;

  if (!isOpen) return null;

  // Header title based on current view
  const headerTitle = (() => {
    if (view === 'chat' && activeConversation) {
      return activeConversation.name || t('conversation.directMessage');
    }
    if (view === 'newConversation') {
      return t('create.title');
    }
    return t('nav.title');
  })();

  const showBackBtn = view !== 'sidebar';

  return (
    <div className={styles.chatTray}>
      {/* Header */}
      <div className={styles.trayHeader}>
        {showBackBtn && (
          <button type="button" className={styles.trayBackBtn} onClick={handleBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}

        <span className={styles.trayTitle}>{headerTitle}</span>

        <div className={styles.trayActions}>
          <button
            type="button"
            className={styles.trayActionBtn}
            onClick={onClose}
            title={t('tray.close')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className={styles.trayBody}>
        {view === 'sidebar' && (
          <ChatSidebar
            conversations={enrichedConversations}
            currentUserGuid={currentUserGuid}
            onSelectConversation={handleSelectConversation}
            onNewConversation={handleNewConversation}
            onArchiveConversation={handleArchiveConversation}
            onPinConversation={handlePinConversation}
            loading={loading}
            hideTitle
          />
        )}

        {view === 'newConversation' && (
          <NewConversationPanel
            users={platformUsers}
            currentUserGuid={currentUserGuid}
            onConversationCreated={handleConversationCreated}
            onCancel={handleBack}
          />
        )}

        {view === 'chat' && activeConversation && (
          <ChatWindow
            conversation={activeConversation}
            currentUserGuid={currentUserGuid}
            onConversationUpdated={() => {
              // DEV-1391 — refresh after an in-window change (e.g. group avatar).
              fetchConversation(activeConversation.id).then(setActiveConversation).catch(() => {});
              fetchConversations().then(setConversations).catch(() => {});
            }}
          />
        )}
      </div>
    </div>
  );
}
